import React, { useEffect, useState } from "react";
import {
  Alert, Badge, Button, CheckCircle, Checkbox, DatePicker, Mail01, MessageSquare01, Select, XCircle,
} from "@opendate-app/setlist";

import { useBroadcastEditContext } from "./BroadcastEditContainer";
import BroadcastSidebar from "./BroadcastSidebar";
import SegmentFilters from "./SegmentFilters";
import { defaultPickedDayToNoon } from "./scheduleDate";

var moment = require('moment-timezone');

const styles = {
  page: {
    color: "var(--sl-text-primary)",
  },
  title: {
    margin: 0,
    fontSize: 20,
    fontWeight: 600,
  },
  text: {
    margin: "8px 0 0",
    color: "var(--sl-text-secondary)",
  },
  section: {
    display: "flex",
    flexDirection: "column",
    gap: 12,
    paddingTop: 24,
    marginTop: 24,
    borderTop: "1px solid var(--sl-border-secondary)",
  },
  sectionHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
  },
  sectionTitle: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontWeight: 600,
  },
  hint: {
    margin: "4px 0 0 28px",
    fontSize: 12,
    color: "var(--sl-text-tertiary)",
  },
  field: {
    fontSize: 12,
    color: "var(--sl-text-tertiary)",
  },
  fieldValue: {
    margin: "2px 0 0",
    fontSize: 14,
    color: "var(--sl-text-primary)",
  },
  preview: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
    width: "100%",
    maxWidth: 400,
    padding: 16,
    textAlign: "left",
    font: "inherit",
    color: "var(--sl-text-primary)",
    backgroundColor: "var(--sl-bg-primary)",
    border: "1px solid var(--sl-border-secondary)",
    borderRadius: 8,
    cursor: "pointer",
  },
  previewTitle: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 8,
    fontWeight: 600,
  },
  schedule: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "flex-start",
    gap: 24,
    marginTop: 16,
  },
  scheduleFields: {
    display: "flex",
    flexDirection: "column",
    gap: 16,
    flex: "0 1 320px",
  },
  footer: {
    display: "flex",
    justifyContent: "flex-end",
    gap: 8,
    marginTop: 80,
  },
};

const timeZones = moment.tz.names().map(tz => ({ value: tz, label: tz }));

const StatusIcon = ({ ok }) => (
  ok ? (
    <CheckCircle width={20} height={20} role="img" aria-label="Ready" style={{ color: "var(--sl-fg-success)" }} />
  ) : (
    <XCircle width={20} height={20} role="img" aria-label="Needs attention" style={{ color: "var(--sl-fg-error)" }} />
  )
);

const SectionHeader = ({ ok, title, onEdit }) => (
  <div style={styles.sectionHeader}>
    <div style={styles.sectionTitle}>
      <StatusIcon ok={ok} />
      {title}
    </div>
    <Button size="sm" onClick={onEdit}>Edit</Button>
  </div>
);

const ReviewAndScheduleTab = () => {
  const {
    team,
    venueOwnership,
    setTab,
    broadcast,
    subject,
    updateBroadcast,
    availableSendDomains,
    timeZone,
    setTimeZone,
    selectedSendDomain,
    fetchRecipientCount,
    recipientsCount,
    checkTemplateHasContent,
    templateHasContent,
    scheduledAt,
    setScheduledAt,
    scheduleBroadcast,
    isEmailTemplate,
  } = useBroadcastEditContext();

  const [transactional, setTransactional] = useState(broadcast.transactional);

  useEffect(() => {
    checkTemplateHasContent();
    fetchRecipientCount();
  }, []);

  const showWarning = (
    !templateHasContent
      || recipientsCount === "--"
  );

  const canSchedule = (
    templateHasContent
      && recipientsCount !== "--"
      && (isEmailTemplate ? selectedSendDomain : broadcast.twilio_template.twilio_messaging_service_id)
      && scheduledAt
      && timeZone
  );

  const editEmailContent = () => (
    templateHasContent || subject !== null ? setTab("content") : setTab("content-select")
  );

  return (
    <BroadcastSidebar>
      <div style={styles.page}>
        <h3 style={styles.title}>Review & Schedule</h3>
        <p style={styles.text}>Verify that everything is set up correctly.</p>
        <div style={styles.section}>
          <SectionHeader ok={recipientsCount !== "--"} title="Recipients" onEdit={() => setTab("recipients")} />
          {recipientsCount === "--" ? (
            <p style={styles.text}>No recipients have been selected. Click the edit button to get started.</p>
          ) : (
            <p style={styles.text}>Your broadcast will be sent to people who match this condition, a total of <strong>{recipientsCount}</strong> people.</p>
          )}
          {broadcast.segment_type === "existing" ? (
            <SegmentFilters editable={false} showAddButton={false} />
          ) : null}
          <div>
            <Checkbox
              id="transactional"
              label="Transactional Message"
              checked={transactional}
              onChange={(e) => {
                setTransactional(e.target.checked);
                updateBroadcast({ broadcast: { transactional: e.target.checked } });
              }}
            />
            <p style={styles.hint}>This message is of a transactional nature and will be sent to those subscribed and unsubscribed.</p>
            <p style={styles.hint}>Make sure to follow <a href="https://en.wikipedia.org/wiki/Email_spam_legislation_by_country" target="_blank" rel="noopener noreferrer">local laws</a> about sending to unsubscribed people.</p>
          </div>
        </div>
        {isEmailTemplate ? (
          <div style={styles.section}>
            <SectionHeader ok={!!selectedSendDomain} title="From" onEdit={() => setTab("from")} />
            <p style={styles.text}>Your newsletter will be sent from:</p>
            <div>
              <div style={styles.field}>Name</div>
              <p style={styles.fieldValue}>{selectedSendDomain?.name || ""}</p>
            </div>
            <div>
              <div style={styles.field}>Email</div>
              <p style={styles.fieldValue}>{selectedSendDomain?.full_from_address || ""}</p>
            </div>
            {availableSendDomains.length === 0 ? (
              <Alert
                color="error"
                role="alert"
                title="Your sending domain has not been setup yet."
                description={<a href={`/teams/${team.id}/marketing_settings?venue_ownership_id=${venueOwnership.id}&tab=email`}>Get started</a>}
              />
            ) : null}
            {!selectedSendDomain ? (
              <Alert color="error" role="alert" title="You have not selected a sending domain to use for this broadcast." />
            ) : null}
          </div>
        ) : null}
        {isEmailTemplate ? (
          <div style={styles.section}>
            <SectionHeader ok={templateHasContent && subject !== null} title="Content" onEdit={editEmailContent} />
            <p style={styles.text}>The following email will send to recipients.</p>
            <button type="button" style={styles.preview} onClick={editEmailContent}>
              <span style={styles.previewTitle}>
                <Mail01 width={16} height={16} />
                {broadcast.name}
                {!templateHasContent ? (
                  <Badge color="gray" size="sm">No content</Badge>
                ) : null}
                {subject === null ? (
                  <Badge color="gray" size="sm">No subject</Badge>
                ) : null}
              </span>
            </button>
            {!templateHasContent ? (
              <Alert color="error" role="alert" title="Add content to your broadcast." />
            ) : null}
            {subject === null ? (
              <Alert color="error" role="alert" title="Add a subject to your broadcast." />
            ) : null}
          </div>
        ) : (
          <div style={styles.section}>
            <SectionHeader
              ok={templateHasContent && broadcast.twilio_template?.twilio_messaging_service_id}
              title="Content"
              onEdit={() => setTab("twilio-editor")}
            />
            <p style={styles.text}>The following message will send to recipients.</p>
            <button type="button" style={styles.preview} onClick={() => setTab("twilio-editor")}>
              <span style={styles.previewTitle}>
                <MessageSquare01 width={16} height={16} />
                {broadcast.name}
                {!templateHasContent ? (
                  <Badge color="gray" size="sm">No content</Badge>
                ) : null}
              </span>
              {templateHasContent ? (
                <span style={{ fontSize: 14 }}>{broadcast.twilio_template.template}</span>
              ) : null}
            </button>
          </div>
        )}
        <div style={styles.section}>
          <h3 style={styles.title}>Schedule</h3>
          <p style={{ ...styles.text, margin: 0 }}>{`When should we send this ${isEmailTemplate ? "email" : "message"}?`}</p>
          <div style={styles.schedule}>
            <div style={styles.scheduleFields}>
              <DatePicker
                time
                label="Date / Time"
                hint="Required"
                placeholder="Select date"
                min={moment().startOf("day").toDate()}
                value={scheduledAt}
                onChange={date => setScheduledAt(defaultPickedDayToNoon(scheduledAt, date))}
              />
              <Select
                searchable
                label="Timezone"
                hint="Required"
                options={timeZones}
                value={timeZone}
                onChange={value => setTimeZone(value)}
              />
            </div>
            {showWarning ? (
              <div style={{ flex: "0 1 400px" }}>
                <Alert
                  color="warning"
                  title="We're not able to send just yet..."
                  description="Review and fix your newsletter before sending."
                />
              </div>
            ) : null}
          </div>
        </div>
        <div style={styles.footer}>
          <Button
            variant="link-gray"
            onClick={() => setTab(isEmailTemplate ? "content-select" : "twilio-content")}
          >
            Back
          </Button>
          <Button
            variant="primary"
            disabled={!canSchedule}
            onClick={e => {
              e.preventDefault();

              scheduleBroadcast(() => {
                window.location = `/teams/${team.id}/broadcasts/${broadcast.id}/edit`;
              });
            }}
          >
            Schedule
          </Button>
        </div>
      </div>
    </BroadcastSidebar>
  );
};

export default ReviewAndScheduleTab;
